import React, { useState, useEffect, useCallback } from 'react';
import {
  Form,
  Button,
  Card,
  Row,
  Col,
  Checkbox,
  Typography,
  Space,
  message,
  Divider,
  Input,
  Tag,
  Spin,
  Card as AntCard,
  InputNumber,
} from 'antd';
import {
  UserOutlined,
  MedicineBoxOutlined,
  IdcardOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  PhoneOutlined,
  LockOutlined,
  MailOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { helper } from '@src/controls/controlHelper';
import SingleSelect from '@src/packages/pro-component/schema/SingleSelect';
import ImageModel from '@src/packages/pro-component/schema/Image';
import RichText from '@src/packages/pro-component/schema/RichText';
import TextArea from 'antd/lib/input/TextArea';

const { Title, Text } = Typography;
const PAGE_ID = 327; // New page ID for doctor management

// Interface for Time Slot
interface TimeSlot {
  id: string;
  time: string;
  isAvailable: boolean;
}

interface DaySlot {
  id: number;
  day: string;
  isAvailable: boolean;
}

// Interface for Time Slot Selector props
interface TimeSlotSelectorProps {
  value?: TimeSlot[];
  onChange?: (value: TimeSlot[]) => void;
}

interface DaySlotSelectorProps {
  value?: DaySlot[];
  onChange?: (value: DaySlot[]) => void;
}

export default function CreateDoctor(props: any) {
  const { onClose } = props;
  const params = props.query || {};
  const { page, mode, id } = params;
  const [pageId, setPageId] = useState<number>(page || PAGE_ID);
  const [form] = Form.useForm();
  const [pageInfo, setPageInfo] = useState<any>();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const isUpdate = mode === 'update';

  // Generate time slots from 8:00 to 18:00 with 15-minute intervals
  const generateTimeSlots = (): TimeSlot[] => {
    const slots: TimeSlot[] = [];
    const startHour = 8;
    const endHour = 18;

    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time = `${hour.toString().padStart(2, '0')}:${minute
          .toString()
          .padStart(2, '0')}`;
        slots.push({
          id: time,
          time,
          isAvailable: true,
        });
      }
    }
    return slots;
  };

  const generateDaySlots = (): DaySlot[] => {
    const slots: DaySlot[] = [];
    const days = [
      { id: 1, day: 'Thứ Hai' },
      { id: 2, day: 'Thứ Ba' },
      { id: 3, day: 'Thứ Tư' },
      { id: 4, day: 'Thứ Năm' },
      { id: 5, day: 'Thứ Sáu' },
      { id: 6, day: 'Thứ Bảy' },
      { id: 0, day: 'Chủ Nhật' },
    ];
    days.forEach((day) => {
      slots.push({
        id: day.id,
        day: day.day,
        isAvailable: true,
      });
    });
    return slots;
  };

  const getDayName = (dayId: number) => {
    const days = [
      { id: 1, day: 'Thứ Hai' },
      { id: 2, day: 'Thứ Ba' },
      { id: 3, day: 'Thứ Tư' },
      { id: 4, day: 'Thứ Năm' },
      { id: 5, day: 'Thứ Sáu' },
      { id: 6, day: 'Thứ Bảy' },
      { id: 0, day: 'Chủ Nhật' },
    ];
    return days.find((d) => d.id === dayId)?.day || '';
  };

  useEffect(() => {
    const initData = async () => {
      try {
        const _pageInfo = await helper.getPage(pageId);
        setPageInfo(_pageInfo);

        if (_pageInfo) {
          if (isUpdate && id) {
            await getDoctorInfo(id, _pageInfo);
          } else {
            const defaultDaySlots = generateDaySlots();
            const defaultTimeSlots = generateTimeSlots();
            form.setFieldsValue({
              dailySchedule: defaultDaySlots,
              weeklySchedule: defaultTimeSlots,
              isActive: true,
              isBookOnline: true,
              isBookNow: true,
            });
          }

          setDataLoaded(true);
        }
      } catch (error) {
        console.log('Error initializing data: ', error);
        message.error('Có lỗi xảy ra khi tải thông tin');
      }
    };

    initData();
  }, []);

  const getDoctorInfo = useCallback(
    async (doctorId: number, _pageInfo: any) => {
      try {
        const response: any = await helper.callPageApi(
          _pageInfo,
          'findDoctor',
          {
            queryInput: JSON.stringify({ id: doctorId }),
            skip: 0,
            limit: 1,
            count: false,
          }
        );

        if (response.status === 200) {
          const { doctorDetail, ...data } = response.data.data[0];

          let notAvailableTimeSlots: any[] = [];
          let defaultTimeSlots = generateTimeSlots();
          let notAvailableDaySlots: any[] = [];
          let defaultDaySlots = generateDaySlots();
          if (doctorDetail.dailySchedule) {
            try {
              if (Array.isArray(doctorDetail.dailySchedule)) {
                notAvailableDaySlots = doctorDetail.dailySchedule.map(
                  (d: any) => ({
                    id: d,
                    day: getDayName(d),
                    isAvailable: true,
                  })
                );
              } else {
                const parsedDaySlots = JSON.parse(doctorDetail.dailySchedule);
                notAvailableDaySlots = parsedDaySlots.map((d: number) => ({
                  id: d,
                  day: getDayName(d),
                  isAvailable: true,
                }));
              }
              defaultDaySlots = defaultDaySlots.map((d: any) => {
                const isAvailable = !notAvailableDaySlots.some(
                  (nd: any) => nd.id === d.id
                );
                return {
                  ...d,
                  isAvailable,
                };
              });
            } catch (e) {
              console.log('Không thể phân tích dữ liệu daySchedule:', e);
              defaultDaySlots = generateDaySlots();
            }
          }
          if (doctorDetail.weeklySchedule) {
            try {
              if (Array.isArray(doctorDetail.weeklySchedule)) {
                notAvailableTimeSlots = doctorDetail.weeklySchedule.map(
                  (t: any) => ({
                    id: t,
                    time: t,
                    isAvailable: true,
                  })
                );
              } else {
                const parsedTimeSlots = JSON.parse(doctorDetail.weeklySchedule);
                notAvailableTimeSlots = parsedTimeSlots.map((t: any) => ({
                  id: t,
                  time: t,
                  isAvailable: true,
                }));
              }
              defaultTimeSlots = defaultTimeSlots.map((t: any) => {
                const isAvailable = !notAvailableTimeSlots.some(
                  (nt: any) => nt.id === t.id
                );
                return {
                  ...t,
                  isAvailable,
                };
              });
            } catch (e) {
              console.log('Không thể phân tích dữ liệu weeklySchedule:', e);
              defaultTimeSlots = generateTimeSlots();
            }
          } else {
            defaultTimeSlots = generateTimeSlots();
          }

          form.setFieldsValue({
            name: data.name,
            title: data.name,
            phone: data.phone,
            email: data.email,
            specialityId: doctorDetail.specialityId,
            avatar: data.avatar,
            degreeId: doctorDetail.degreeId,
            licenseNumber: doctorDetail.licenseNumber || '',
            price: doctorDetail.price || 0,
            experience: doctorDetail.experience || 0,
            hospitalId: doctorDetail.hospitalId,
            shortDescription: doctorDetail.shortDescription,
            description: doctorDetail.description,
            isActive: data.status,
            isBookOnline: doctorDetail.isBookOnline,
            isBookNow: doctorDetail.isBookNow,
            dailySchedule: defaultDaySlots,
            weeklySchedule: defaultTimeSlots,
            degreeImage: doctorDetail.degreeImage || null,
            certificateImage: doctorDetail.certificateImage || null,
          });
        } else {
          throw new Error('Failed to fetch doctor info');
        }
      } catch (error) {
        console.log('Error fetching doctor info: ', error);
        message.error('Có lỗi xảy ra khi tải thông tin bác sĩ');
      }
    },
    [form]
  );

  const onFinish = useCallback(async () => {
    try {
      setLoading(true);
      const formData = form.getFieldsValue();

      const response: any = isUpdate
        ? await helper.callPageApi(pageInfo, 'updateDoctor', {
            id,
            ...formData,
          })
        : await helper.callPageApi(pageInfo, 'createDoctor', formData);

      if (response.status === 200) {
        message.success(
          isUpdate
            ? 'Cập nhật thông tin bác sĩ thành công'
            : 'Tạo mới bác sĩ thành công'
        );
        form.resetFields();
        onClose();
      } else {
        throw new Error(isUpdate ? 'Failed to update' : 'Failed to create');
      }
    } catch (error: any) {
      console.log('Error creating/updating doctor: ', error);
      message.error('Có lỗi xảy ra khi lưu thông tin bác sĩ');
    } finally {
      setLoading(false);
    }
  }, [pageInfo, isUpdate, id, form, onClose]);

  const handleCancel = () => {
    onClose();
  };

  const DaySlotSelector: React.FC<DaySlotSelectorProps> = ({
    value = [],
    onChange,
  }) => {
    const handleDaySlotToggle = (slotId: number) => {
      const newSlots = value.map((slot) =>
        slot.id === slotId ? { ...slot, isAvailable: !slot.isAvailable } : slot
      );
      onChange?.(newSlots);
    };

    const availableSlots = value.filter((slot) => slot.isAvailable).length;
    const totalSlots = value.length;

    return (
      <div>
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <Title level={5} style={{ marginBottom: 16 }}>
              <CalendarOutlined style={{ marginRight: 8 }} />
              NGÀY LÀM VIỆC TRONG TUẦN
            </Title>
            <Tag color='processing'>{`${availableSlots}/${totalSlots} ngày khả dụng`}</Tag>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: 8,
          }}
        >
          {value.map((slot) => (
            <Button
              key={slot.id}
              type={slot.isAvailable ? 'default' : 'dashed'}
              size='small'
              onClick={() => handleDaySlotToggle(slot.id)}
              style={{
                height: '32px',
                fontSize: '12px',
                ...(slot.isAvailable
                  ? {
                      backgroundColor: '#f0f9ff',
                      borderColor: '#1890ff',
                      color: '#1890ff',
                    }
                  : {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#d9d9d9',
                      color: '#8c8c8c',
                    }),
              }}
            >
              {slot.day}
            </Button>
          ))}
        </div>
      </div>
    );
  };

  // Time Slot Selector Component
  const TimeSlotSelector: React.FC<TimeSlotSelectorProps> = ({
    value = [],
    onChange,
  }) => {
    const handleTimeSlotToggle = (slotId: string) => {
      const newSlots = value.map((slot) =>
        slot.id === slotId ? { ...slot, isAvailable: !slot.isAvailable } : slot
      );
      onChange?.(newSlots);
    };

    const availableSlots = value.filter((slot) => slot.isAvailable).length;
    const totalSlots = value.length;

    return (
      <div>
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            {/* Professional Information */}
            <Title level={5} style={{ marginBottom: 16 }}>
              <ClockCircleOutlined style={{ marginRight: 8 }} />
              KHUNG GIỜ TƯ VẤN
            </Title>
            <Tag color='processing'>{`${availableSlots}/${totalSlots} khung giờ khả dụng`}</Tag>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: 8,
          }}
        >
          {value.map((slot) => (
            <Button
              key={slot.id}
              type={slot.isAvailable ? 'default' : 'dashed'}
              size='small'
              onClick={() => handleTimeSlotToggle(slot.id)}
              style={{
                height: '32px',
                fontSize: '12px',
                ...(slot.isAvailable
                  ? {
                      backgroundColor: '#f0f9ff',
                      borderColor: '#1890ff',
                      color: '#1890ff',
                    }
                  : {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#d9d9d9',
                      color: '#8c8c8c',
                    }),
              }}
            >
              {slot.time}
            </Button>
          ))}
        </div>

        <div style={{ marginTop: 8, textAlign: 'right' }}>
          <Text type='secondary' style={{ fontSize: '12px' }}>
            Click vào khung giờ để bật/tắt khả dụng
          </Text>
        </div>
      </div>
    );
  };

  if (!dataLoaded) {
    return (
      <div className='text-center p-5'>
        <Spin size='large' tip='Đang tải dữ liệu...' />
      </div>
    );
  }

  return (
    <div className='doctor-form-container'>
      <Form
        form={form}
        layout='vertical'
        onFinish={onFinish}
        className='doctor-form'
      >
        <Card className='mb-4 custom-card'>
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                name='avatar'
                label={
                  <div>
                    Ảnh đại diện
                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                      Kích thước khuyến nghị: 300x300 px
                    </div>
                  </div>
                }
                rules={[
                  { required: true, message: 'Vui lòng chọn ảnh đại diện' },
                ]}
              >
                <ImageModel
                  onChange={(value) => {
                    form.setFieldsValue({
                      avatar: value,
                    });
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name='name'
                label='Họ và tên'
                rules={[{ required: true, message: 'Vui lòng nhập họ và tên' }]}
              >
                <Input
                  placeholder='Nhập họ và tên'
                  prefix={<UserOutlined />}
                  size='large'
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name='phone'
                label='Số điện thoại'
                rules={[
                  { required: true, message: 'Vui lòng nhập số điện thoại' },
                ]}
              >
                <Input
                  placeholder='Nhập số điện thoại'
                  prefix={<PhoneOutlined />}
                  size='large'
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name='email'
                label='Email'
                rules={[{ required: false, message: 'Vui lòng nhập email' }]}
              >
                <Input
                  placeholder='Nhập email'
                  prefix={<MailOutlined />}
                  size='large'
                />
              </Form.Item>
            </Col>
            {!isUpdate && (
              <>
                <Col xs={24} md={12}>
                  <Form.Item
                    name='password'
                    label='Mật khẩu'
                    rules={[
                      { required: true, message: 'Vui lòng nhập mật khẩu' },
                    ]}
                  >
                    <Input
                      placeholder='Nhập mật khẩu'
                      prefix={<LockOutlined />}
                      size='large'
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name='rePassword'
                    label='Nhập lại mật khẩu'
                    rules={[
                      { required: true, message: 'Vui lòng nhập lại mật khẩu' },
                    ]}
                  >
                    <Input
                      placeholder='Nhập lại mật khẩu'
                      prefix={<LockOutlined />}
                      size='large'
                    />
                  </Form.Item>
                </Col>
              </>
            )}
            <Col xs={24} md={24}>
              <Form.Item
                name='shortDescription'
                label='Giới thiệu ngắn'
                rules={[
                  {
                    required: true,
                    message: 'Vui lòng nhập nội dung giới thiệu ngắn',
                  },
                ]}
              >
                <TextArea
                  placeholder='Nhập nội dung giới thiệu'
                  rows={4}
                  maxLength={255}
                  showCount
                  onBlur={(e) => {
                    form.setFieldsValue({
                      shortDescription: e.target.value,
                    });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider />
          {/* Consultation Day Slots */}
          <Form.Item name='dailySchedule'>
            <DaySlotSelector />
          </Form.Item>
          {/* Consultation Time Slots */}
          <Form.Item name='weeklySchedule'>
            <TimeSlotSelector />
          </Form.Item>

          <Divider />

          {/* Professional Information */}
          <Title level={5} style={{ marginBottom: 16 }}>
            <MedicineBoxOutlined style={{ marginRight: 8 }} />
            THÔNG TIN CHUYÊN KHOA
          </Title>

          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                name='degreeId'
                label='Trình độ'
                rules={[{ required: true, message: 'Vui lòng chọn trình độ' }]}
              >
                <SingleSelect
                  schema={{
                    pageId: pageId,
                    api: 'findDegree',
                    modelSelectField: 'id$$ID,name$$Trình độ',
                  }}
                  placeholder='Chọn trình độ'
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name='specialityId'
                label='Chuyên khoa'
                rules={[
                  { required: true, message: 'Vui lòng chọn chuyên khoa' },
                ]}
              >
                <SingleSelect
                  schema={{
                    pageId: pageId,
                    api: 'findSpeciality',
                    modelSelectField: 'id$$ID,name$$Chuyên khoa',
                  }}
                  placeholder='Chọn chuyên khoa'
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                name='licenseNumber'
                label={
                  <span>
                    <IdcardOutlined style={{ marginRight: 4 }} />
                    Số giấy chứng chỉ hành nghề
                  </span>
                }
                rules={[
                  { required: true, message: 'Vui lòng nhập số chứng chỉ' },
                ]}
              >
                <Input placeholder='Nhập số chứng chỉ hành nghề' />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name='price'
                label={
                  <span>
                    <DollarOutlined style={{ marginRight: 4 }} />
                    Giá tư vấn (VNĐ)
                  </span>
                }
                rules={[
                  { required: true, message: 'Vui lòng nhập giá tư vấn' },
                ]}
              >
                <InputNumber
                  placeholder='Nhập giá tư vấn'
                  style={{ width: '100%' }}
                  defaultValue={0}
                  min={0}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item name='degreeImage' label='Bằng cấp'>
                <ImageModel />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name='certificateImage' label='Giấy phép hành nghề'>
                <ImageModel />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          {/* Experience & Bio */}
          <Title level={5} style={{ marginBottom: 16 }}>
            <ClockCircleOutlined style={{ marginRight: 8 }} />
            KINH NGHIỆM & THÔNG TIN BỔ SUNG
          </Title>

          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                name='experience'
                label='Số năm kinh nghiệm'
                rules={[
                  {
                    required: false,
                    message: 'Vui lòng nhập số năm kinh nghiệm',
                  },
                ]}
              >
                <InputNumber
                  placeholder='Ví dụ: 14'
                  style={{ width: '100%' }}
                  min={0}
                  max={50}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name='hospitalId'
                label='Nơi công tác'
                rules={[
                  { required: true, message: 'Vui lòng nhập nơi công tác' },
                ]}
              >
                <SingleSelect
                  schema={{
                    pageId: pageId,
                    api: 'findHospital',
                    modelSelectField: 'id$$ID,name$$Phòng khám/Bệnh viện',
                  }}
                  placeholder='Chọn nơi làm việc'
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name='description'
            label='Thông tin bổ sung'
            tooltip='Mô tả chi tiết về kinh nghiệm, chuyên môn, thành tựu...'
          >
            <RichText
              onChange={(value) => {
                form.setFieldsValue({
                  description: value,
                });
              }}
            />
          </Form.Item>

          <Divider />

          {/* Settings */}
          <Row gutter={12}>
            <Col xs={12} md={4}>
              <Form.Item
                name='isActive'
                valuePropName='checked'
                initialValue={true}
              >
                <Checkbox>Kích hoạt bác sĩ</Checkbox>
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item
                name='isBookOnline'
                valuePropName='checked'
                initialValue={true}
              >
                <Checkbox>Cho phép đặt lịch Online</Checkbox>
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item
                name='isBookNow'
                valuePropName='checked'
                initialValue={true}
              >
                <Checkbox>Cho phép tư vấn ngay</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <div
          className='d-flex justify-content-end mt-4'
          style={{ textAlign: 'right', marginTop: 16 }}
        >
          <Space>
            <Button type='primary' htmlType='submit' loading={loading}>
              {isUpdate ? 'Cập nhật thông tin' : 'Tạo mới bác sĩ'}
            </Button>
            <Button onClick={handleCancel}>Hủy</Button>
          </Space>
        </div>
      </Form>
    </div>
  );
}
